import type {SanityClient} from '@sanity/client'
import {validatePreviewUrl} from '@sanity/preview-url-secret'
import {perspectiveCookieName, variantCookieName} from '@sanity/preview-url-secret/constants'
import {cookies, draftMode} from 'next/headers'
import {redirect} from 'next/navigation'

import {partitionedCookieName} from '#live/constants'

/**
 * @public
 */
export interface DefineEnableDraftModeOptions {
  client: SanityClient
  /**
   * Force secure cookies in development mode.
   * Enable this when using Next.js --experimental-https flag.
   * This option has no effect in production (cookies are always secure).
   * @defaultValue false
   */
  secureDevMode?: boolean
}

/**
 * @public
 */
export interface EnableDraftMode {
  GET: (request: Request) => Promise<Response>
}

/**
 * Sets up an API route for enabling draft mode, can be paired with the `previewUrl.previewMode.enable` in `sanity/presentation`.
 * Can also be used with `sanity-plugin-iframe-pane`.
 *
 * When the preview loads in a cross-site iframe (Presentation Tool), draft-mode
 * cookies are set with the CHIPS `Partitioned` attribute so Safari 18.4+ stores
 * them despite third-party cookie blocking. Top-level / same-site requests keep
 * unpartitioned cookies so `draftMode().disable()` continues to work.
 *
 * @see https://github.com/sanity-io/sanity/issues/12806
 *
 * @example
 * ```ts
 * // src/app/api/draft-mode/enable/route.ts
 *
 * import { defineEnableDraftMode } from "next-sanity/draft-mode";
 * import { client } from "@/sanity/lib/client";
 *
 * export const { GET } = defineEnableDraftMode({
 *   client: client.withConfig({ token: process.env.SANITY_API_READ_TOKEN }),
 * });
 * ```
 *
 * @public
 */
export function defineEnableDraftMode(options: DefineEnableDraftModeOptions): EnableDraftMode {
  const {client} = options
  return {
    GET: async (request: Request) => {
      // @TODO check if already in draft mode at a much earlier stage, and skip validation

      const {
        isValid,
        redirectTo = '/',
        studioPreviewPerspective,
        studioPreviewVariant,
      } = await validatePreviewUrl(client, request.url)
      if (!isValid) {
        return new Response('Invalid secret', {status: 401})
      }

      const draftModeStore = await draftMode()

      // Let's enable draft mode if it's not already enabled
      if (!draftModeStore.isEnabled) {
        draftModeStore.enable()
      }

      const isProduction = process.env.NODE_ENV === 'production'

      // We can't auto-detect HTTPS in dev due to Next.js limitations,
      // so we need an explicit option
      const isSecure = isProduction || (options.secureDevMode ?? false)

      // Safari blocks unpartitioned third-party cookies. When the enable route is
      // hit from a cross-site iframe (Presentation), opt into CHIPS so the cookies
      // are stored under the studio's partition. Skip partitioning for top-level /
      // same-site requests so draftMode().disable() can still clear them.
      // https://github.com/sanity-io/sanity/issues/12806
      const partitioned =
        isSecure &&
        request.headers.get('sec-fetch-dest') === 'iframe' &&
        request.headers.get('sec-fetch-site') === 'cross-site'

      // Override cookie header for draft mode for usage in live-preview
      // https://github.com/vercel/next.js/issues/49927
      const cookieStore = await cookies()
      const cookie = cookieStore.get('__prerender_bypass')!
      cookieStore.set({
        name: '__prerender_bypass',
        value: cookie?.value,
        httpOnly: true,
        path: '/',
        secure: isSecure,
        sameSite: isSecure ? 'none' : 'lax',
        partitioned,
      })

      if (studioPreviewPerspective) {
        cookieStore.set({
          name: perspectiveCookieName,
          value: studioPreviewPerspective,
          httpOnly: true,
          path: '/',
          secure: isSecure,
          sameSite: isSecure ? 'none' : 'lax',
          partitioned,
        })
      }

      if (studioPreviewVariant) {
        cookieStore.set({
          name: variantCookieName,
          value: studioPreviewVariant,
          httpOnly: true,
          path: '/',
          secure: isSecure,
          sameSite: isSecure ? 'none' : 'lax',
          partitioned,
        })
      } else {
        // Unlike perspective, the variant is optional on the enable URL. Entering
        // preview without a variant must clear any stale variant cookie from a
        // previous session, passing matching attributes so partitioned cookies
        // are actually removed.
        cookieStore.delete({
          name: variantCookieName,
          httpOnly: true,
          path: '/',
          secure: isSecure,
          sameSite: isSecure ? 'none' : 'lax',
          partitioned,
        })
      }

      // Persist the partitioning decision for later writes (server actions have no
      // Sec-Fetch iframe signal). The flag cookie is itself partitioned, so it is
      // only visible inside the same cross-site iframe context.
      if (partitioned) {
        cookieStore.set({
          name: partitionedCookieName,
          value: '1',
          httpOnly: true,
          path: '/',
          secure: true,
          sameSite: 'none',
          partitioned: true,
        })
      }

      return redirect(redirectTo)
    },
  }
}
